import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchShopItems,
  submitCheckout,
  ShopConflictError,
  type CheckoutResult,
  type ShopItemDto,
} from '../api';
import { loadUser } from '../pexesoStore';
import { navigate } from '../router';
import { siteConfig } from '../siteConfig';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Footer } from '../components/Footer';
import { visualFor } from './shopVisuals';
import './ShopPage.css';

const CART_KEY = 'wedding.shop.cart.v1';
const GATE_KEY = 'wedding.shop.unlocked.v2';
const MAX_PER_ITEM = 10;

type Cart = Record<string, number>;

function loadCartFromStorage(): Cart {
  try {
    const raw = window.localStorage.getItem(CART_KEY);
    return raw ? (JSON.parse(raw) as Cart) : {};
  } catch {
    return {};
  }
}

function saveCartToStorage(cart: Cart) {
  try {
    window.localStorage.setItem(CART_KEY, JSON.stringify(cart));
  } catch {
    /* best effort */
  }
}

function maxFor(item: ShopItemDto): number {
  return item.remaining == null ? MAX_PER_ITEM : Math.min(MAX_PER_ITEM, item.remaining);
}

export function ShopPage() {
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState<ShopItemDto[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [cart, setCart] = useState<Cart>(() => loadCartFromStorage());
  const [cartOpen, setCartOpen] = useState(false);
  const [buyerName, setBuyerName] = useState(() => loadUser()?.name ?? '');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<'conflict' | 'server' | null>(null);
  const [result, setResult] = useState<CheckoutResult | null>(null);
  /* Destinační fotky jedou z Pexels CDN — když se některá nenačte,
     karta spadne zpět na typografický poster. */
  const [brokenPhotos, setBrokenPhotos] = useState<Record<string, boolean>>({});
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    if (!siteConfig.pages.shop.underConstruction) return true;
    try {
      return window.localStorage.getItem(GATE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [gateInput, setGateInput] = useState('');

  const handleGateInput = (value: string) => {
    setGateInput(value);
    if (value.trim() === siteConfig.pages.shop.unlockCode) {
      setUnlocked(true);
      try {
        window.localStorage.setItem(GATE_KEY, '1');
      } catch {
        /* best effort */
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const refresh = async () => {
    try {
      setItems(await fetchShopItems());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  /* Po každém refreshi skladu clampni košík na to, co reálně zbývá. */
  useEffect(() => {
    if (!items) return;
    setCart((c) => {
      let changed = false;
      const next: Cart = {};
      for (const [slug, qty] of Object.entries(c)) {
        const item = items.find((i) => i.slug === slug);
        if (!item) {
          changed = true;
          continue;
        }
        const clamped = Math.min(qty, maxFor(item));
        if (clamped !== qty) changed = true;
        if (clamped > 0) next[slug] = clamped;
      }
      return changed ? next : c;
    });
  }, [items]);

  const bySlug = useMemo(() => new Map((items ?? []).map((i) => [i.slug, i])), [items]);

  const cartLines = Object.entries(cart).filter(([slug, qty]) => qty > 0 && bySlug.has(slug));
  const cartCount = cartLines.reduce((sum, [, qty]) => sum + qty, 0);
  /* EN/DE platí v eurech (pevný kurz z backendu), ostatní jazyky v Kč. */
  const lang = (i18n.language.split('-')[0] || 'cs').toLowerCase();
  const isEur = lang === 'en' || lang === 'de';
  const displayCurrency = isEur ? 'EUR' : 'CZK';
  const priceOf = (item: ShopItemDto) => (isEur ? item.priceEur : item.priceCzk);

  const cartTotal = cartLines.reduce((sum, [slug, qty]) => {
    const it = bySlug.get(slug);
    return sum + qty * (it ? priceOf(it) : 0);
  }, 0);

  const formatAmount = (amount: number, currency: string) =>
    `${amount.toLocaleString(i18n.language)} ${currency === 'EUR' ? '€' : 'Kč'}`;

  const formatIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

  const setQty = (slug: string, qty: number) => {
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[slug];
      else next[slug] = qty;
      return next;
    });
  };

  /* Grid „Podpořit" = přepínač výběru (stock 1). Znovu-klik položku
     zase odebere. Dokončení daru jde přes spodní lištu nebo košík. */
  const toggle = (slug: string) => {
    setCart((c) => {
      const next = { ...c };
      if ((c[slug] ?? 0) > 0) delete next[slug];
      else next[slug] = 1;
      return next;
    });
  };

  const handleCheckout = async (e: FormEvent) => {
    e.preventDefault();
    if (!buyerName.trim() || cartLines.length === 0 || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitCheckout({
        buyerName: buyerName.trim(),
        buyerEmail: buyerEmail.trim(),
        message: message.trim(),
        language: i18n.language.split('-')[0] || 'cs',
        lines: cartLines.map(([slug, quantity]) => ({ slug, quantity })),
      });
      setResult(res);
      setCart({});
      void refresh();
    } catch (err) {
      if (err instanceof ShopConflictError) {
        setSubmitError('conflict');
        await refresh();
      } else {
        setSubmitError('server');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeCart = () => {
    setCartOpen(false);
    if (result) setResult(null);
  };

  const itemTitle = (slug: string) => t(`shop.items.${slug}.title`);

  if (!unlocked) {
    return (
      <div className="shop">
        <header className="shop-header">
          <button type="button" className="shop-header__back" onClick={() => navigate('/')}>
            ← {t('shop.backHome')}
          </button>
          <div className="shop-header__mark">{siteConfig.event.coupleShort}</div>
          <div className="shop-header__tools">
            <LanguageSwitcher />
          </div>
        </header>
        <main className="shop-main shop-gate">
          <div className="section-kicker">{t('shop.kicker')}</div>
          <h1 className="shop-hero__title">{t('shop.construction.title')}</h1>
          <p className="shop-hero__intro">{t('shop.construction.text')}</p>
          <div className="shop-gate__sign" aria-hidden="true">🚧</div>
          <input
            className="shop-gate__input"
            type="password"
            value={gateInput}
            autoComplete="off"
            aria-label="code"
            onChange={(e) => handleGateInput(e.target.value)}
          />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="shop">
      <header className="shop-header">
        <button type="button" className="shop-header__back" onClick={() => navigate('/')}>
          ← {t('shop.backHome')}
        </button>
        <div className="shop-header__mark">{siteConfig.event.coupleShort}</div>
        <div className="shop-header__tools">
          <LanguageSwitcher />
          <button
            type="button"
            className="shop-header__cart"
            onClick={() => setCartOpen(true)}
            aria-label={t('shop.cart')}
          >
            🛒
            {cartCount > 0 && <span className="shop-header__cart-badge">{cartCount}</span>}
          </button>
        </div>
      </header>

      <main className="shop-main">
        <div className="shop-hero">
          <div className="section-kicker">{t('shop.kicker')}</div>
          <h1 className="shop-hero__title">{t('shop.title')}</h1>
          <p className="shop-hero__intro">{t('shop.intro')}</p>
        </div>

        {loadError && (
          <div className="shop-error">
            <p>{t('shop.loadError')}</p>
            <button type="button" className="btn btn--ghost" onClick={() => void refresh()}>
              {t('shop.retry')}
            </button>
          </div>
        )}

        {!items && !loadError && <div className="shop-loading">{t('shop.loading')}</div>}

        {items && (
          <div className="gift-grid">
            {items.map((item) => {
              const visual = visualFor(item.slug);
              const photoSrc = brokenPhotos[item.slug] ? undefined : visual.photo;
              const claimed = item.remaining !== null && item.remaining <= 0;
              const picked = (cart[item.slug] ?? 0) > 0;
              return (
                <article
                  key={item.slug}
                  className={`gift-card ${claimed ? 'gift-card--done' : ''} ${
                    picked ? 'gift-card--picked' : ''
                  }`}
                >
                  <div
                    className={`gift-card__media ${photoSrc ? '' : 'gift-card__media--empty'}`}
                    title={photoSrc ? undefined : visual.word}
                  >
                    {photoSrc ? (
                      <img
                        className="gift-card__img"
                        src={photoSrc}
                        alt=""
                        loading="lazy"
                        onError={() =>
                          setBrokenPhotos((b) => ({ ...b, [item.slug]: true }))
                        }
                      />
                    ) : (
                      <span className="gift-card__ph" aria-hidden="true">🖼️</span>
                    )}
                    <span
                      className={`gift-card__box ${claimed || picked ? 'is-checked' : ''}`}
                      aria-hidden="true"
                    >
                      {claimed || picked ? '✓' : ''}
                    </span>
                    {claimed && <span className="gift-card__ribbon">{t('shop.claimed')}</span>}
                  </div>

                  <div className="gift-card__body">
                    <h3 className="gift-card__name">{itemTitle(item.slug)}</h3>
                    <p className="gift-card__desc">{t(`shop.items.${item.slug}.desc`)}</p>
                    <div className="gift-card__footer">
                      <span className="gift-card__price">{formatAmount(priceOf(item), displayCurrency)}</span>
                      {claimed ? (
                        <span className="gift-card__done-label">✓ {t('shop.claimed')}</span>
                      ) : (
                        <button
                          type="button"
                          className={`btn gift-card__btn ${picked ? 'btn--ghost' : ''}`}
                          onClick={() => toggle(item.slug)}
                        >
                          {picked ? t('shop.remove') : t('shop.support')}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {cartCount > 0 && (
        <div className="gift-bar">
          <span className="gift-bar__info">{cartCount}× · {formatAmount(cartTotal, displayCurrency)}</span>
          <button type="button" className="btn gift-bar__btn" onClick={() => setCartOpen(true)}>
            {t('shop.finish')}
          </button>
        </div>
      )}

      <Footer />

      {cartOpen && (
        <div className="shop-cart-overlay" onClick={closeCart}>
          <aside className="shop-cart" onClick={(e) => e.stopPropagation()}>
            <div className="shop-cart__head">
              <h2>{result ? t('shop.success.title') : t('shop.cart')}</h2>
              <button type="button" className="shop-cart__close" onClick={closeCart} aria-label="×">
                ×
              </button>
            </div>

            {result ? (
              <div className="shop-cart__success">
                <p>{t('shop.success.text')}</p>
                <ul className="shop-cart__lines">
                  {result.lines.map((line) => (
                    <li key={line.slug} className="shop-cart__line">
                      <span className="shop-cart__line-title">
                        {itemTitle(line.slug)} ×{line.quantity}
                      </span>
                      <span className="shop-cart__line-price">
                        {formatAmount(line.price * line.quantity, result.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="shop-cart__total">
                  <span>{t('shop.total')}</span>
                  <strong>{formatAmount(result.total, result.currency)}</strong>
                </div>

                {result.payment && (
                  <div className="shop-pay">
                    <h3 className="shop-pay__title">{t('shop.payment.title')}</h3>
                    <div
                      className="shop-pay__qr"
                      role="img"
                      aria-label={t('shop.payment.qrAlt')}
                      dangerouslySetInnerHTML={{ __html: result.payment.qrSvg }}
                    />
                    <p className="shop-pay__scan">{t('shop.payment.scan')}</p>
                    <dl className="shop-pay__details">
                      <div className="shop-pay__row">
                        <dt>{t('shop.payment.account')}</dt>
                        <dd>{formatIban(result.payment.iban)}</dd>
                      </div>
                      {result.payment.bic && (
                        <div className="shop-pay__row">
                          <dt>{t('shop.payment.bic')}</dt>
                          <dd>{result.payment.bic}</dd>
                        </div>
                      )}
                      <div className="shop-pay__row">
                        <dt>{t('shop.payment.amount')}</dt>
                        <dd>{formatAmount(result.payment.amount, result.payment.currency)}</dd>
                      </div>
                      <div className="shop-pay__row">
                        <dt>{t('shop.payment.vs')}</dt>
                        <dd>{result.payment.reference}</dd>
                      </div>
                      <div className="shop-pay__row">
                        <dt>{t('shop.payment.recipient')}</dt>
                        <dd>{result.payment.recipient}</dd>
                      </div>
                    </dl>
                  </div>
                )}

                <p className="shop-cart__payment-note">{t('shop.success.paymentNote')}</p>
                <button type="button" className="btn" onClick={closeCart}>
                  {t('shop.backToShop')}
                </button>
              </div>
            ) : cartLines.length === 0 ? (
              <p className="shop-cart__empty">{t('shop.cartEmpty')}</p>
            ) : (
              <>
                <ul className="shop-cart__lines">
                  {cartLines.map(([slug, qty]) => {
                    const item = bySlug.get(slug)!;
                    return (
                      <li key={slug} className="shop-cart__line">
                        <span className="shop-cart__line-title">{itemTitle(slug)}</span>
                        <div className="shop-qty">
                          <button type="button" onClick={() => setQty(slug, qty - 1)}>
                            −
                          </button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            disabled={qty >= maxFor(item)}
                            onClick={() => setQty(slug, qty + 1)}
                          >
                            +
                          </button>
                        </div>
                        <span className="shop-cart__line-price">
                          {formatAmount(priceOf(item) * qty, displayCurrency)}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="shop-cart__total">
                  <span>{t('shop.total')}</span>
                  <strong>{formatAmount(cartTotal, displayCurrency)}</strong>
                </div>

                {submitError && (
                  <p className="shop-cart__error">
                    {submitError === 'conflict' ? t('shop.errors.conflict') : t('shop.errors.server')}
                  </p>
                )}

                <form className="shop-checkout" onSubmit={handleCheckout}>
                  <h3>{t('shop.checkout.title')}</h3>
                  <label>
                    {t('shop.checkout.name')}
                    <input
                      type="text"
                      value={buyerName}
                      maxLength={64}
                      required
                      placeholder={t('shop.checkout.namePlaceholder')}
                      onChange={(e) => setBuyerName(e.target.value)}
                    />
                  </label>
                  <label>
                    {t('shop.checkout.email')}
                    <input
                      type="email"
                      value={buyerEmail}
                      maxLength={160}
                      placeholder={t('shop.checkout.emailPlaceholder')}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                    />
                  </label>
                  <label>
                    {t('shop.checkout.message')}
                    <textarea
                      value={message}
                      maxLength={500}
                      rows={3}
                      placeholder={t('shop.checkout.messagePlaceholder')}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </label>
                  <button
                    type="submit"
                    className="btn shop-checkout__submit"
                    disabled={submitting || !buyerName.trim() || cartLines.length === 0}
                  >
                    {submitting ? t('shop.checkout.submitting') : t('shop.checkout.submit')}
                  </button>
                </form>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
